import React, { useMemo, useRef, useState, useEffect } from 'react';
import { textIncludesQuery, textsEqualForSearch } from '@narrativebench/editor-core';
import { Input } from './ui/input';
import { Plus } from 'lucide-react';
import { isSystemTag } from '../utils/system-tags';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

export interface ProjectTagsDropdownProps {
  selectedTags: string[];
  availableTags: string[];
  onTagsChange: (tags: string[]) => void | Promise<void>;
  disabled?: boolean;
  initialOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
  inputPlaceholder: string;
  createLabel: (tag: string) => string;
  trigger: (params: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    selectedTags: string[];
    removeTag: (tag: string) => void;
  }) => React.ReactNode;
}

export const ProjectTagsDropdown: React.FC<ProjectTagsDropdownProps> = ({
  selectedTags,
  availableTags,
  onTagsChange,
  disabled = false,
  initialOpen = false,
  onOpenChange,
  contentClassName,
  inputPlaceholder,
  createLabel,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [tagSearch, setTagSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  useEffect((): void | (() => void) => {
    if (!isOpen) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const normalizedSelectedTags = useMemo(
    () => selectedTags.map(normalizeTag),
    [selectedTags]
  );

  const options = useMemo(() => {
    const tagSet = new Set<string>();
    for (const tag of availableTags) {
      const normalized = normalizeTag(tag);
      if (normalized && !isSystemTag(normalized)) {
        tagSet.add(normalized);
      }
    }
    for (const tag of selectedTags) {
      const normalized = normalizeTag(tag);
      if (normalized && !isSystemTag(normalized)) {
        tagSet.add(normalized);
      }
    }
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [availableTags, selectedTags]);

  const filteredOptions = useMemo(() => {
    if (!tagSearch.trim()) {
      return options;
    }
    return options.filter((tag) => textIncludesQuery(tag, tagSearch));
  }, [options, tagSearch]);

  const normalizedSearchTag = useMemo(
    () => normalizeTag(tagSearch),
    [tagSearch]
  );

  const canCreateTag =
    normalizedSearchTag.length > 0 &&
    !options.some((tag) => textsEqualForSearch(tag, tagSearch)) &&
    !isSystemTag(normalizedSearchTag);

  const setTagSelected = (tag: string, shouldSelect: boolean): void => {
    const normalized = normalizeTag(tag);
    if (!normalized) {
      return;
    }
    const nextTags = shouldSelect
      ? Array.from(new Set([...normalizedSelectedTags, normalized]))
      : normalizedSelectedTags.filter((t) => t !== normalized);
    void Promise.resolve(onTagsChange(nextTags));
  };

  const removeTag = (tag: string): void => {
    const normalized = normalizeTag(tag);
    if (!normalized) {
      return;
    }
    void Promise.resolve(
      onTagsChange(normalizedSelectedTags.filter((t) => t !== normalized))
    );
  };

  const addTagFromSearch = (): void => {
    const normalized = normalizeTag(tagSearch);
    if (!normalized || isSystemTag(normalized)) {
      return;
    }
    void Promise.resolve(
      onTagsChange(Array.from(new Set([...normalizedSelectedTags, normalized])))
    );
    setTagSearch('');
  };

  return (
    <DropdownMenu
      modal={false}
      open={disabled ? false : isOpen}
      onOpenChange={(open) => {
        if (disabled) {
          return;
        }
        setIsOpen(open);
        onOpenChange?.(open);
        if (!open) {
          setTagSearch('');
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <div>
          {trigger({ isOpen, setIsOpen, selectedTags: normalizedSelectedTags, removeTag })}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={dropdownContentRef}
        align="start"
        className={cn('w-[230px] max-w-[230px] p-2', contentClassName)}
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={inputPlaceholder}
          value={tagSearch}
          onChange={(e) => setTagSearch(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              addTagFromSearch();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              const firstItem = dropdownContentRef.current?.querySelector<HTMLElement>(
                '[role="menuitemcheckbox"]:not([disabled]), [role="menuitem"]:not([disabled])'
              );
              firstItem?.focus();
            }
          }}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canCreateTag}
          onSelect={(e) => {
            e.preventDefault();
            addTagFromSearch();
          }}
        >
          <Plus className="h-4 w-4" />
          {createLabel(normalizedSearchTag)}
        </DropdownMenuItem>
        {filteredOptions.length > 0 && <DropdownMenuSeparator />}
        <div className="max-h-[260px] overflow-auto">
          {filteredOptions.map((tag) => {
            const checked = normalizedSelectedTags.includes(tag);
            return (
              <DropdownMenuCheckboxItem
                key={tag}
                checked={checked}
                onCheckedChange={(nextChecked) => {
                  setTagSelected(tag, Boolean(nextChecked));
                }}
              >
                <span>{tag}</span>
              </DropdownMenuCheckboxItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

